import { PriceType, SupportedLanguage } from "interfaces";
import { isEmpty, isNumber } from "lodash";
import moment from "moment";
import { languageSchema } from "routes/event/schema";
import { z } from "zod";

export const ParentTicketSchema = (languages: SupportedLanguage[]) => {
  return z.discriminatedUnion("is_package", [
    z.object({
      is_package: z.literal(true),
      category: languageSchema(languages),
      selling_min_qty: z
        .number({
          invalid_type_error: "Quantity must be a number",
          required_error: "This field is required"
        })
        .min(1, {message:"Quantity must be equal or greater than 1"})
        .nonnegative({ message: "Quantity must be a positive number" }),
      selling_max_qty: z
        .number({
          invalid_type_error: "Quantity must be a number",
          required_error: "This field is required"
        })
        .min(1, {message:"Quantity must be equal or greater than 1"})
        .nonnegative({ message: "Quantity must be a positive number" }),
      attendant_template: z.array(
        z.object({
          template_id: z.string().min(1, "This field is required"),
          name: z.object({
            en: z.string().optional(),
            vi: z.string().optional()
          }),
        })
      ).min(2, "At least 2 attendant templates are required for a package ticket"),
    }),
    z.object({
      is_package: z.literal(false),
      category: languageSchema(languages),
      selling_min_qty: z
        .number({
          invalid_type_error: "Quantity must be a number",
          required_error: "This field is required"
        })
        .min(1, {message:"Quantity must be equal or greater than 1"})
        .nonnegative({ message: "Quantity must be a positive number" }),
      selling_max_qty: z
        .number({
          invalid_type_error: "Quantity must be a number",
          required_error: "This field is required"
        })
        .min(1, {message:"Quantity must be equal or greater than 1"})
        .nonnegative({ message: "Quantity must be a positive number" }),
      attendant_template: z.array(
        z.object({
          template_id: z.string().min(1, "This field is required"),
          name: z.object({
            en: z.string().optional(),
            vi: z.string().optional()
          }),
        })
      ).min(1, "This field is required"),
    })
  ])
  .refine((data) => data.selling_min_qty <= data.selling_max_qty, {
    message: "Max quantity cannot be smaller than min quantity.",
    path: ["selling_max_qty"],
  })
  .superRefine(({selling_min_qty, selling_max_qty},ctx) => {
    if (selling_max_qty < selling_min_qty) {
      ctx.addIssue({
        path: ["selling_max_qty"],
        code: "custom",
        message: "Selling max quantity cannot be smaller than Selling min quantity",
      });
    }
  })
}

export const SubTicketSchema = (languages: SupportedLanguage[]) => {

  const ticketSchema = (priceType: PriceType) => {
    return z.object({
      price_type: z.literal(priceType),
      type_name: languageSchema(languages),
      short_description: z.object({
        en: z.string().optional(),
        vi: z.string().optional()
      }),
      original_price: priceType === PriceType.PAID 
        ? z
        .number({
          invalid_type_error: "Price must be a number",
          required_error: "This field is required"
        })
        .positive({message: "Price must be greater than 0"})
        .nonnegative({ message: "Price must be a positive number" })
        : z
        .number({
          invalid_type_error: "Price must be a number",
          required_error: "This field is required"
        })
        .nonnegative({ message: "Price must be a positive number" }),
      price: priceType === PriceType.PAID 
        ? z
        .number({
          invalid_type_error: "Price must be a number",
          required_error: "This field is required"
        })
        .positive({message: "Price must be greater than 0"})
        .nonnegative({ message: "Price must be a positive number" })
        : z
        .number({
          invalid_type_error: "Price must be a number",
          required_error: "This field is required"
        })
        .nonnegative({ message: "Price must be a positive number" }),
      sell_from: z.string().min(1, "This field is required"),
      sell_to: z.string().min(1, "This field is required"),
      hide_in_ui: z.boolean(),
      is_soldout: z.boolean(),
      is_active: z.boolean(),
      ticket_phase: z.union([
        z.string().min(1, "This field is required"),
        z
        .number({
          invalid_type_error: "Phase must be a number",
        })
      ]),
      total_for_sell: z.union([z.number().int().min(0, "Quantity must be equal or greater than 0").nullable(), z.nan()]).optional() 
    })
  };

  return z.discriminatedUnion("price_type",[
    ticketSchema(PriceType.FREE),
    ticketSchema(PriceType.PAID)
  ])
  .refine((data) => moment(data.sell_from).isSameOrBefore(moment(data.sell_to)), {
    message: "End date cannot be earlier than start date.",
    path: ["sell_to"],
  })
  .refine((data) => data.original_price >= data.price, {
    message: "Original price cannot be smaller than sell price.",
    path: ["original_price"],
  })
}



export const NoParentTicketSchema = (languages: SupportedLanguage[]) => {
  const ticketSchema = (priceType: PriceType) => {
    return z.object({
      price_type: z.literal(priceType),
      category: languageSchema(languages),
      type_name: languageSchema(languages),
      short_description: z.object({
        en: z.string().optional(),
        vi: z.string().optional()
      }),
      selling_min_qty: z
      .number({
        invalid_type_error: "Quantity must be a number",
        required_error: "This field is required"
      })
      .min(1, {message:"Quantity must be equal or greater than 1"})
      .nonnegative({ message: "Quantity must be a positive number" }),
      selling_max_qty: z
      .number({
        invalid_type_error: "Quantity must be a number",
        required_error: "This field is required"
      })
      .min(1, {message:"Quantity must be equal or greater than 1"})
      .nonnegative({ message: "Quantity must be a positive number" }),
      original_price: priceType === PriceType.PAID 
        ? z
        .number({
          invalid_type_error: "Price must be a number",
          required_error: "This field is required"
        })
        .positive({message: "Price must be greater than 0"})
        .nonnegative({ message: "Price must be a positive number" })
        : z
        .number({
          invalid_type_error: "Price must be a number",
          required_error: "This field is required"
        })
        .nonnegative({ message: "Price must be a positive number" }),
      price: priceType === PriceType.PAID 
        ? z
        .number({
          invalid_type_error: "Price must be a number",
          required_error: "This field is required"
        })
        .positive({message: "Price must be greater than 0"})
        .nonnegative({ message: "Price must be a positive number" })
        : z
        .number({
          invalid_type_error: "Price must be a number",
          required_error: "This field is required"
        })
        .nonnegative({ message: "Price must be a positive number" }),
      sell_from: z.string().min(1, "This field is required"),
      sell_to: z.string().min(1, "This field is required"),
      attendant_template: z.array(
        z.object({
          template_id: z.string().min(1, "This field is required"),
          name: z.object({
            en: z.string().optional(),
            vi: z.string().optional()
          }),
        })
      ).min(1, "This field is required"),
      hide_in_ui: z.boolean(),
      is_package: z.boolean(),
      is_soldout: z.boolean(),
      is_active: z.boolean(),
      ticket_phase: z.string(),
      total_for_sell: z.union([z.number().int().min(0, "Total for sell must be equal or greater than 0").nullable(), z.nan()]).optional(),
      is_auto_seating: z.boolean(),
      start_seat_number: z.union([z.number().int().min(1, "Minimum number of seats must be equal or greater than 1").nullable(), z.nan()]).optional(),
      auto_seating_leading_zero_spaces: z.union([z.number().int().min(1, "Number must be equal or greater than 1").max(6, "Number must be equal or smaller than 6").nullable(), z.nan()]).optional(),
      auto_seating_add_characters: z.boolean(),
      auto_seating_characters: z.string().optional(),
      auto_seating_character_position: z.string().optional(),
      layout_type:  z.enum(['zone', 'seat']),
      seat_layout_id: z.string().optional().nullable(),
      category_color: z.string().optional().nullable(),
    })
  };

  return z.discriminatedUnion("price_type",[
    ticketSchema(PriceType.FREE),
    ticketSchema(PriceType.PAID)
  ])
  .refine((data) => moment(data.sell_from).isSameOrBefore(moment(data.sell_to)), {
    message: "End date cannot be earlier than start date.",
    path: ["sell_to"],
  })
  .refine((data) => data.selling_min_qty <= data.selling_max_qty, {
    message: "Max quantity cannot be smaller than min quantity.",
    path: ["selling_max_qty"],
  })
  .refine((data) => data.original_price >= data.price, {
    message: "Original price cannot be smaller than sell price.",
    path: ["original_price"],
  })
  .superRefine(({selling_min_qty, selling_max_qty, is_auto_seating,total_for_sell, start_seat_number, auto_seating_leading_zero_spaces, auto_seating_add_characters, auto_seating_characters, auto_seating_character_position},ctx) => {
    if (selling_max_qty < selling_min_qty) {
      ctx.addIssue({
        path: ["selling_max_qty"],
        code: "custom",
        message: "Selling max quantity cannot be smaller than Selling min quantity",
      });
    }
    if (is_auto_seating) {
      if (!isNumber(total_for_sell) || isNaN(total_for_sell)) {
        ctx.addIssue({
          path: ["total_for_sell"],
          code: "custom",
          message: "Total for sell must be a number",
        });
      } else if (total_for_sell < 1) {
        ctx.addIssue({
          path: ["total_for_sell"],
          code: "custom",
          message: "Total for sell must be equal or greater than 1",
        });
      } else {
        if (isNumber(auto_seating_leading_zero_spaces) && !isNaN(auto_seating_leading_zero_spaces)) {
          if (auto_seating_leading_zero_spaces < total_for_sell.toString().length) {
            ctx.addIssue({
              path: ["auto_seating_leading_zero_spaces"],
              code: "custom",
              message: `Please enter a number from ${total_for_sell.toString().length}`,
            });
          }
        }
      }
      if (!isNumber(start_seat_number) || isNaN(start_seat_number)) {
        ctx.addIssue({
          path: ["start_seat_number"],
          code: "custom",
          message: "Minimum number of seats must be a number",
        });
      } else if (start_seat_number < 1) {
        ctx.addIssue({
          path: ["start_seat_number"],
          code: "custom",
          message: "Minimum number of seats must be equal or greater than 1",
        });
      }
      if (auto_seating_add_characters) {
        if (isEmpty(auto_seating_character_position)) {
          ctx.addIssue({
            path: ["auto_seating_character_position"],
            code: "custom",
            message: "This field is required",
          });
        }
        if (isEmpty(auto_seating_characters)) {
          ctx.addIssue({
            path: ["auto_seating_characters"],
            code: "custom",
            message: "This field is required",
          });
        } else {
          if ((auto_seating_characters ?? "").length > 10 ) {
            ctx.addIssue({
              path: ["auto_seating_characters"],
              code: "custom",
              message: "Characters must be equal or smaller than 10",
            });
          }
        }
      }
    }
  })
};
